import { fakeFetchUrl } from '../../../app-core/lib/file/withS3Url'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { AppUserProfile } from '../../../shared/domain/users/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import { DS_setData } from '../DS/DS.Data'
import { IDB_userProfiles } from '../IDB/IDB.dexie'
import { Imag_cache, getFromCache, isAlreadyCached, removeFromCache } from '../Imag/Imag.cache'
import { LS_userSet } from '../LS/LS.user'
import { User_get } from './User.get'

/*
 * We want to cache the user profile images so that they are available while offline.
 * We use the existing `Image.cache` infrastructure for caching the image in IDB,
 * but we don't want to use the related `Imag_CachedImage` component because the
 * profile image gets used inside of `zaptic-profile-circle`, which is a shared web component.
 * So instead we hook-in to the user profile sync, and generate the object URLs for the cached blobs
 * immediately and store this as the `profileImageUrl` value in the profiles stored in IDB.
 *
 * The lifetime of object URLs are tied to the document, so every time the app is started (or page reloaded)
 * we need to re-generate the object URLs for the cached blobs.
 *
 * For the current user we cache the full size image, but for other users we cache the thumbnail.  This is
 * because when the current user updates their profile image, we want to show them the updated result (including
 * server-side processing, eg cropping) immediately, but there can be a delay before the thumbnail is generated.
 */

function isBlobUrl(url: string) {
    return url.startsWith('blob:')
}

let _clientLogger: ClientLogger
function getLogger() {
    return _clientLogger || (_clientLogger = Base_commonDeps().createLogger('UserProfileImageCache'))
}

const fileIdToBlobUrlMap = new Map<FileId, string>()

export function getCachedBlobUrl(fileId: FileId) {
    return fileIdToBlobUrlMap.get(fileId)
}

/** If user profiles have previously been synced, and the app has re-started
 *  then any object URLs we've generated for the cached profile images will
 *  no longer be valid (because object URL lifetime is tied to the document).
 *  So re-generate object URLs for existing cached profile images.
 */
export async function reInitialise() {
    const allUserProfiles = await IDB_userProfiles().toArray()

    const currentUserId = Base_commonDeps().user.id

    await Promise.allSettled(
        allUserProfiles.map(async (userProfile) => {
            if (userProfile.profileInfo.image) {
                const useThumbnail = userProfile.userId !== currentUserId
                const blobUrl = await getBlobUrl(userProfile.profileInfo.image, true, useThumbnail)
                userProfile.profileImageUrl = blobUrl
            } else {
                userProfile.profileImageUrl = null
            }
        })
    )
    await IDB_userProfiles().bulkPut(allUserProfiles)

    const currentUserProfile = allUserProfiles.find((userProfile) => userProfile.userId === currentUserId)
    if (currentUserProfile) {
        saveProfileImageUrlForCurrentUserLocally(currentUserProfile.profileImageUrl)
    }
}

/** Clear-out the cache of images and associated object URLs.  Intended for use when user is logging-out. */
export function clearCache() {
    for (const [fileId, objectUrl] of fileIdToBlobUrlMap.entries()) {
        URL.revokeObjectURL(objectUrl)
        removeFromCache(fakeFetchUrl(fileId))
    }

    fileIdToBlobUrlMap.clear()
}

export async function onCurrentUserUdatedProfileImage(fileId: FileId): Promise<string | null> {
    const currentUserId = Base_commonDeps().user.id
    const [userProfile] = await IDB_userProfiles().where('userId').equals(currentUserId).toArray()

    userProfile.profileInfo.image = fileId
    const useThumbnail = false
    userProfile.profileImageUrl = fileId ? await getBlobUrl(fileId, false, useThumbnail) : null

    await IDB_userProfiles().put(userProfile)

    return userProfile.profileImageUrl
}

/** keeps track of whether we are currently processing profile images, to avoid multiple concurrent runs.
 * (a run is triggered by every sync of user profiles)
 */
let currentlyProcessing = false

export async function ensureProfileImagesCached() {
    if (currentlyProcessing) {
        return
    }

    try {
        currentlyProcessing = true
        await internalEnsureProfileImagesCached()
    } catch (error) {
        getLogger().warn('Failed to cache profile images', { error })
    } finally {
        currentlyProcessing = false
    }
}

async function internalEnsureProfileImagesCached() {
    const allUserProfiles = await IDB_userProfiles().toArray()

    // put current user at the start of the array, to increase the chance of being cached before
    // the user goes offline:
    const currentUserId = Base_commonDeps().user.id
    const currentUserIndex = allUserProfiles.findIndex((userProfile) => userProfile.userId === currentUserId)
    const [currentUser] = allUserProfiles.splice(currentUserIndex, 1)
    allUserProfiles.unshift(currentUser)

    for (const userProfile of allUserProfiles) {
        const online = await Base_commonDeps().isOnline()
        if (!online) {
            // can't cache images while offline => exit early
            return
        }

        try {
            const isCurrentUser = userProfile.userId === currentUserId
            await ensureProfileImageCachedForUser(userProfile, isCurrentUser)
        } catch (error) {
            // might not be a big deal, e.g. user might have just gone offline
            getLogger().warn('Failed to cache profile image', {
                error,
                user: {
                    id: userProfile.userId,
                    email: userProfile.email,
                    profileImageUrl: userProfile.profileImageUrl,
                },
            })
        }
    }

    await removeUnusedImagesFromCache()
}

async function ensureProfileImageCachedForUser(userProfile: AppUserProfile, isCurrentUser: boolean) {
    if (userProfile.profileImageUrl && isBlobUrl(userProfile.profileImageUrl)) {
        // already cached image for this user => skip
        return
    }

    if (!userProfile.profileInfo.image && !userProfile.profileImageUrl) {
        // user does not have a profile image (and didn't last time either) => skip
        return
    }

    if (userProfile.profileInfo.image) {
        const useThumbnail = !isCurrentUser
        const blobUrl = await getBlobUrl(userProfile.profileInfo.image, false, useThumbnail)
        userProfile.profileImageUrl = blobUrl
    } else {
        userProfile.profileImageUrl = null
    }
    await IDB_userProfiles().put(userProfile)

    if (isCurrentUser) {
        saveProfileImageUrlForCurrentUserLocally(userProfile.profileImageUrl)
    }
}

function saveProfileImageUrlForCurrentUserLocally(profileImageUrl: string) {
    const user = User_get()
    user.profileImageUrl = profileImageUrl

    DS_setData('Profile Image cached', () => ({ user }))
    LS_userSet(user)
}

/** Get the object URL for the blob for the specified {@linkcode fileId}.  If such object URL has already been generated
 * then that will be returned (ie from the cache).  Otherwise will try to get the blob itself and generate the object URL.
 */
async function getBlobUrl(fileId: FileId, onlyIfAlreadyCached: boolean, thumbnail: boolean): Promise<string | null> {
    if (fileIdToBlobUrlMap.has(fileId)) {
        return fileIdToBlobUrlMap.get(fileId)
    }

    const imageBlob = await getBlob(fileId, onlyIfAlreadyCached, thumbnail)
    if (!imageBlob) {
        return null
    }
    const blobUrl = URL.createObjectURL(imageBlob)
    fileIdToBlobUrlMap.set(fileId, blobUrl)
    return blobUrl
}

/** Get the blob for the specified {@linkcode fileId}; if it's already been cached then return that, otherwise by default
 * will try to download it, unless {@linkcode onlyIfAlreadyCached} is set to true (in which case download will not be attempted).
 */
async function getBlob(fileId: FileId, onlyIfAlreadyCached: boolean, thumbnail: boolean): Promise<Blob | null> {
    const fetchUrl = fakeFetchUrl(fileId)
    if (await isAlreadyCached(fetchUrl)) {
        return getFromCache(fetchUrl)
    }

    if (onlyIfAlreadyCached) {
        return null
    }

    const imageBlob = await Imag_cache(fetchUrl, true, thumbnail)
    if (!imageBlob) {
        throw new Error(`Failed to cache image: ${fileId}`)
    }
    return imageBlob
}

/** In case any users have updated their profile image, remove old images from the cache */
async function removeUnusedImagesFromCache() {
    const allUserProfiles = await IDB_userProfiles().toArray()
    const usedFileIds = new Set(allUserProfiles.map((userProfile) => userProfile.profileInfo.image).filter(Boolean))

    const fileIdsToRemoveFromCache = Array.from(fileIdToBlobUrlMap.keys()).filter((fileId) => !usedFileIds.has(fileId))
    for (const fileId of fileIdsToRemoveFromCache) {
        const blobUrl = fileIdToBlobUrlMap.get(fileId)
        URL.revokeObjectURL(blobUrl)
        removeFromCache(fakeFetchUrl(fileId))
        fileIdToBlobUrlMap.delete(fileId)
    }
}
